import { Button } from '@/components/ui/button';
import { Share2, Facebook, Twitter, Mail, Copy } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';

interface SocialSharingButtonProps {
  projectName: string;
  contributionCount: number;
  projectType: string;
  className?: string;
}

export function SocialSharingButton({ 
  projectName, 
  contributionCount, 
  projectType, 
  className = "" 
}: SocialSharingButtonProps) {

  const getImpactMessage = (type: string, count: number): string => {
    switch (type) {
      case 'trees':
        return `🌱 Ich habe bereits ${count} Bäume durch meine Empfehlungen gepflanzt!`;
      case 'families':
        return `❤️ Ich habe ${count} Familien durch meine Empfehlungen unterstützt!`;
      case 'education':
        return `📚 Ich habe ${count} Bildungsprojekte durch meine Empfehlungen gefördert!`;
      default:
        return `✨ Ich habe ${count} Beiträge zu sozialen Projekten geleistet!`;
    }
  };

  const shareText = `${getImpactMessage(projectType, contributionCount)} 
  
Projekt: ${projectName}

Mach mit bei der Herzensempfehlung und hilf auch du mit deinen Empfehlungen! 💚`;

  const shareUrl = window.location.origin;

  const handleShare = async (platform: string) => {
    const encodedText = encodeURIComponent(shareText);
    const encodedUrl = encodeURIComponent(shareUrl);

    switch (platform) {
      case 'native':
        if (navigator.share) {
          try {
            await navigator.share({
              title: 'Mein soziales Engagement',
              text: shareText,
              url: shareUrl
            });
          } catch (error) {
            console.log('Sharing cancelled');
          }
        } else {
          await handleShare('copy');
        }
        break;
      
      case 'facebook':
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
          '_blank',
          'width=600,height=400'
        );
        break;
      
      case 'twitter':
        window.open(
          `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
          '_blank',
          'width=600,height=400'
        );
        break;
      
      case 'whatsapp':
        window.open(
          `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
          '_blank'
        );
        break;
      
      case 'email':
        window.open(
          `mailto:?subject=${encodeURIComponent('Mein soziales Engagement')}&body=${encodedText}%0A%0A${encodedUrl}`,
          '_self'
        );
        break;
      
      case 'copy':
        try {
          await navigator.clipboard.writeText(`${shareText}\n\n${shareUrl}`);
          toast.success('Link in die Zwischenablage kopiert!');
        } catch (error) {
          toast.error('Kopieren fehlgeschlagen');
        }
        break;
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className={className}>
          <Share2 className="h-4 w-4 mr-2" />
          Teilen
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onClick={() => handleShare('native')}>
          <Share2 className="h-4 w-4 mr-2" />
          Teilen
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleShare('whatsapp')}>
          <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
          </svg>
          WhatsApp
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleShare('facebook')}>
          <Facebook className="h-4 w-4 mr-2" />
          Facebook
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleShare('twitter')}>
          <Twitter className="h-4 w-4 mr-2" />
          Twitter
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleShare('email')}>
          <Mail className="h-4 w-4 mr-2" />
          E-Mail
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleShare('copy')}>
          <Copy className="h-4 w-4 mr-2" />
          Link kopieren
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}